import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/Empty';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useCompanies, useCreateCompany, type Company } from '@/data/companies';

export const Route = createFileRoute('/admin/companies/')({
  component: CompaniesIndex,
});

const selectClass =
  'mt-1 h-11 min-w-44 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-[var(--color-ink)]';

function CompaniesIndex() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#4f46e5');
  const [season, setSeason] = useState('2026-2027');
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const companyQuery = useMemo(
    () => ({
      search: search || undefined,
      season: seasonFilter || undefined,
      active: statusFilter === 'all' ? undefined : statusFilter === 'active',
    }),
    [search, seasonFilter, statusFilter],
  );
  const companies = useCompanies(companyQuery);
  const createCompany = useCreateCompany();

  const companyRows = useMemo(
    () => (Array.isArray(companies.data) ? companies.data : []),
    [companies.data],
  );
  const displayedCompanies = useMemo(() => {
    if (
      !createCompany.data ||
      companyRows.some((company) => company.id === createCompany.data.id)
    ) {
      return companyRows;
    }
    return [...companyRows, createCompany.data];
  }, [companyRows, createCompany.data]);

  const selectedCompany = useMemo(() => {
    return (
      displayedCompanies.find((company) => company.id === selectedId) ??
      displayedCompanies[0] ??
      null
    );
  }, [displayedCompanies, selectedId]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim() || !season.trim()) return;

    createCompany.mutate(
      { name, color, season, active, notes: notes.trim() || undefined },
      {
        onSuccess: (company) => {
          setSelectedId(company.id);
          setName('');
          setColor('#4f46e5');
          setSeason('2026-2027');
          setActive(true);
          setNotes('');
        },
      },
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Team foundation
          </p>
          <h1 className="mt-2">Companies</h1>
          <p className="mt-1 max-w-2xl text-[var(--color-ink-muted)]">
            Organize studio groups by season for roster, routine, class, and reporting workflows.
          </p>
        </div>
        {displayedCompanies.length > 0 && (
          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {displayedCompanies.filter((company) => company.active).length} active /{' '}
            {displayedCompanies.length} total
          </p>
        )}
      </div>

      <section aria-label="Company filters" className="surface flex flex-wrap items-end gap-4 p-4">
        <div className="min-w-64 flex-1">
          <Label htmlFor="company-search">Search</Label>
          <Input
            id="company-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Company, alias, or note"
          />
        </div>
        <div>
          <Label htmlFor="company-season-filter">Filter by season</Label>
          <Input
            id="company-season-filter"
            value={seasonFilter}
            onChange={(event) => setSeasonFilter(event.target.value)}
            placeholder="2026-2027"
          />
        </div>
        <div>
          <Label htmlFor="company-status-filter">Filter by status</Label>
          <select
            id="company-status-filter"
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value as 'all' | 'active' | 'inactive')
            }
            className={selectClass}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="space-y-4">
          {companies.isLoading && <p aria-live="polite">Loading…</p>}

          {companies.isError && (
            <p role="alert" className="text-[var(--color-danger)]">
              {companies.error instanceof Error
                ? companies.error.message
                : 'Failed to load companies.'}
            </p>
          )}

          {displayedCompanies.length === 0 && !companies.isLoading && !companies.isError && (
            <Empty
              title="No companies yet"
              description="Create the first company to start organizing dancers for assignments."
            />
          )}

          {displayedCompanies.length > 0 && (
            <div role="region" aria-label="Companies table" className="surface overflow-hidden">
              <table className="w-full text-left">
                <caption className="sr-only">Studio companies</caption>
                <thead className="bg-[var(--color-surface-sunken)] text-[var(--text-sm)]">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Company
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Season
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedCompanies.map((company) => (
                    <CompanyRow
                      key={company.id}
                      company={company}
                      selected={selectedCompany?.id === company.id}
                      onSelect={() => setSelectedId(company.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <form
            aria-label="Create company"
            onSubmit={handleSubmit}
            className="surface space-y-4 p-4"
          >
            <div>
              <h2 className="font-[var(--font-display)] text-[var(--text-xl)]">Create company</h2>
              <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                Create a season-scoped group for rosters, routines, classes, and reporting.
              </p>
            </div>

            <div>
              <Label htmlFor="company-name">Name</Label>
              <Input
                id="company-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Petite Company"
                required
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <div>
                <Label htmlFor="company-color">Color</Label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    id="company-color"
                    type="color"
                    value={color}
                    onChange={(event) => setColor(event.target.value)}
                    className="h-11 w-14 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-1"
                    aria-label="Company color"
                  />
                  <Input
                    value={color}
                    onChange={(event) => setColor(event.target.value)}
                    aria-label="Company color hex"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="company-season">Season</Label>
                <Input
                  id="company-season"
                  value={season}
                  onChange={(event) => setSeason(event.target.value)}
                  placeholder="2026-2027"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="company-status">Status</Label>
              <select
                id="company-status"
                value={active ? 'active' : 'inactive'}
                onChange={(event) => setActive(event.target.value === 'active')}
                className={selectClass}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <Label htmlFor="company-notes">Notes</Label>
              <textarea
                id="company-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3.5 py-2 text-[var(--color-ink)] placeholder:text-[var(--color-ink-subtle)] focus-visible:outline-none"
                placeholder="Audition criteria, rehearsal notes, or assignment context"
              />
            </div>

            <Button
              type="submit"
              disabled={createCompany.isPending || !name.trim() || !season.trim()}
            >
              {createCompany.isPending ? 'Creating…' : 'Create company'}
            </Button>
            {createCompany.isError && (
              <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
                {createCompany.error instanceof Error
                  ? createCompany.error.message
                  : "Couldn't create the company"}
              </p>
            )}
          </form>

          <section aria-labelledby="company-detail-heading" className="surface space-y-4 p-4">
            <div>
              <h2
                id="company-detail-heading"
                className="font-[var(--font-display)] text-[var(--text-xl)]"
              >
                Detail
              </h2>
              <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                Season, status, color, and notes for the selected company.
              </p>
            </div>

            {selectedCompany ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-medium">{selectedCompany.name}</p>
                    <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      {selectedCompany.season}
                    </p>
                  </div>
                  <Badge tone={selectedCompany.active ? 'success' : 'neutral'}>
                    {selectedCompany.active ? 'active' : 'inactive'}
                  </Badge>
                </div>

                <div className="grid gap-2 text-[var(--text-sm)]">
                  <p className="flex items-center justify-between gap-3">
                    <span className="text-[var(--color-ink-muted)]">Color</span>
                    <span className="inline-flex items-center gap-2 font-medium">
                      <span
                        aria-label={`${selectedCompany.name} color swatch`}
                        className="inline-block size-4 rounded-full border border-[var(--color-border)]"
                        style={{ backgroundColor: selectedCompany.color }}
                      />
                      {selectedCompany.color}
                    </span>
                  </p>
                  <p>
                    <span className="text-[var(--color-ink-muted)]">Notes: </span>
                    {selectedCompany.notes ?? 'No notes yet.'}
                  </p>
                </div>

                <div className="rounded-[var(--radius-md)] border border-dashed border-[var(--color-border)] bg-[var(--color-surface-sunken)] p-3 text-[var(--text-sm)]">
                  <p className="font-medium">Assignment placeholders</p>
                  <p className="mt-1 text-[var(--color-ink-muted)]">
                    Roster, instructor, routine, and rehearsal assignments will connect here when
                    the full assignment contract lands.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-[var(--color-ink-muted)]">Select or create a company.</p>
            )}
          </section>
        </aside>
      </section>
    </div>
  );
}

function CompanyRow({
  company,
  selected,
  onSelect,
}: {
  company: Company;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <tr className="border-t border-[var(--color-border)] hover:bg-[var(--color-surface-sunken)]">
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          className="inline-flex items-center gap-3 text-left font-medium text-[var(--color-ink)] underline-offset-2 hover:underline"
        >
          <span
            aria-hidden="true"
            className="inline-block size-4 rounded-full border border-[var(--color-border)]"
            style={{ backgroundColor: company.color }}
          />
          {company.name}
        </button>
      </td>
      <td className="px-4 py-3">
        <Badge tone={company.active ? 'success' : 'neutral'}>
          {company.active ? 'active' : 'inactive'}
        </Badge>
      </td>
      <td className="px-4 py-3 text-[var(--color-ink-muted)]">{company.season}</td>
      <td className="max-w-64 px-4 py-3 text-[var(--color-ink-muted)]">
        <span className="line-clamp-2">{company.notes ?? '—'}</span>
      </td>
    </tr>
  );
}
